import OpenAI from 'openai';
import { SYSTEM_PROMPTS } from '../prompts/systemPrompts';
import { DEPLOYMENT_EMAIL_TEMPLATES, PRODUCT_DETAILS, LLM_DETAILS, SAAS_DETAILS, TABLE_INSTRUCTIONS } from '../prompts/deploymentEmailPrompts';

/**
 * Enhanced OpenAI Client with task-specific defaults
 */
class OpenAIClient {
  // Default models for different tasks
  static DEFAULT_MODELS = {
    CHAT: 'gpt-4o',
    CHAT_MINI: 'gpt-4o-mini',
    VISION: 'gpt-4o',
    VISION_DETAILED: 'gpt-4o',
    WEB_SEARCH: 'gpt-4o',
    WEB_SEARCH_MINI: 'gpt-4o-mini',
    EMBEDDING: 'text-embedding-ada-002',
    CODE: 'gpt-4o',
    CREATIVE: 'gpt-4o',
    ANALYSIS: 'gpt-4o'
  };

  // Default system prompts imported from prompts folder
  static SYSTEM_PROMPTS = SYSTEM_PROMPTS;

  constructor(options = {}) {
    // Initialize with API key
    const apiKey = options.apiKey || process.env.REACT_APP_OPENAI_API_KEY;
    
    if (!apiKey) {
      console.warn('OpenAI API key is not configured. Add REACT_APP_OPENAI_API_KEY to your .env file to enable OpenAI features.');
      this.client = null;
    } else {
      this.client = new OpenAI({
        apiKey: apiKey,
        dangerouslyAllowBrowser: options.dangerouslyAllowBrowser !== false
      });
    }

    // Allow overriding default models at instance level
    this.models = {
      ...OpenAIClient.DEFAULT_MODELS,
      ...(options.models || {})
    };

    // Allow overriding system prompts at instance level
    this.systemPrompts = {
      ...OpenAIClient.SYSTEM_PROMPTS,
      ...(options.systemPrompts || {})
    };

    // Default options
    this.defaultOptions = {
      temperature: options.temperature || 0.7,
      maxTokens: options.maxTokens || 500,
      ...(options.defaultOptions || {})
    };
  }

  /**
   * Get the appropriate model for a task
   * @private
   */
  _getModel(taskType, userModel) {
    return userModel || this.models[taskType] || this.models.CHAT;
  }

  /**
   * Send a basic chat completion request
   */
  async getChatCompletion(prompt, options = {}) {
    if (!this.client) {
      throw new Error('OpenAI client is not initialized. Please check your API key configuration.');
    }
    
    try {
      const response = await this.client.chat.completions.create({
        model: this._getModel('CHAT', options.model),
        messages: [
          {
            role: 'system',
            content: options.systemPrompt || this.systemPrompts.GENERAL
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: options.temperature ?? this.defaultOptions.temperature,
        max_tokens: options.maxTokens ?? this.defaultOptions.maxTokens,
        ...options.additionalParams
      });

      return response.choices[0].message.content;
    } catch (error) {
      console.error('Error calling OpenAI API:', error);
      throw new Error(`OpenAI API Error: ${error.message}`);
    }
  }

  /**
   * Send a chat completion for code-related tasks
   */
  async getCodeCompletion(prompt, options = {}) {
    return this.getChatCompletion(prompt, {
      ...options,
      model: this._getModel('CODE', options.model),
      systemPrompt: options.systemPrompt || this.systemPrompts.CODE_ASSISTANT,
      temperature: options.temperature ?? 0.3 // Lower temperature for code
    });
  }

  /**
   * Send a chat completion for creative writing
   */
  async getCreativeCompletion(prompt, options = {}) {
    return this.getChatCompletion(prompt, {
      ...options,
      model: this._getModel('CREATIVE', options.model),
      systemPrompt: options.systemPrompt || this.systemPrompts.CREATIVE_WRITER,
      temperature: options.temperature ?? 0.9 // Higher temperature for creativity
    });
  }

  /**
   * Send a chat completion for analysis tasks
   */
  async getAnalysisCompletion(prompt, options = {}) {
    return this.getChatCompletion(prompt, {
      ...options,
      model: this._getModel('ANALYSIS', options.model),
      systemPrompt: options.systemPrompt || this.systemPrompts.ANALYZER,
      temperature: options.temperature ?? 0.5 // Balanced temperature for analysis
    });
  }

  /**
   * Stream chat completion with appropriate defaults
   */
  async streamChatCompletion(prompt, onChunk, options = {}) {
    try {
      const stream = await this.client.chat.completions.create({
        model: this._getModel('CHAT', options.model),
        messages: [
          {
            role: 'system',
            content: options.systemPrompt || this.systemPrompts.GENERAL
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: options.temperature ?? this.defaultOptions.temperature,
        max_tokens: options.maxTokens ?? this.defaultOptions.maxTokens,
        stream: true,
        ...options.additionalParams
      });

      let fullResponse = '';
      
      for await (const chunk of stream) {
        const content = chunk.choices[0]?.delta?.content || '';
        fullResponse += content;
        
        if (onChunk) {
          onChunk(content, fullResponse);
        }
      }

      return fullResponse;
    } catch (error) {
      console.error('Error streaming from OpenAI API:', error);
      throw new Error(`OpenAI API Error: ${error.message}`);
    }
  }

  /**
   * Enhanced streaming with detailed chunk information
   */
  async streamChatCompletionAdvanced(messages, onChunk, options = {}) {
    try {
      const stream = await this.client.chat.completions.create({
        model: this._getModel('CHAT', options.model),
        messages: messages,
        temperature: options.temperature ?? this.defaultOptions.temperature,
        max_tokens: options.maxTokens ?? this.defaultOptions.maxTokens,
        stream: true,
        ...options.additionalParams
      });

      let fullResponse = '';
      let role = '';
      const chunks = [];
      
      for await (const chunk of stream) {
        chunks.push(chunk);
        
        if (chunk.choices[0]?.delta?.role) {
          role = chunk.choices[0].delta.role;
        }
        
        const content = chunk.choices[0]?.delta?.content || '';
        fullResponse += content;
        
        if (onChunk) {
          onChunk({
            content,
            fullResponse,
            role,
            chunk,
            isFinished: chunk.choices[0]?.finish_reason !== null
          });
        }
      }

      return {
        content: fullResponse,
        role,
        chunks,
        usage: chunks[chunks.length - 1]?.usage
      };
    } catch (error) {
      console.error('Error in advanced streaming:', error);
      throw new Error(`OpenAI API Error: ${error.message}`);
    }
  }

  /**
   * Analyze images using vision capabilities
   */
  async analyzeImage(imageInput, prompt = "What is in this image?", options = {}) {
    try {
      const images = Array.isArray(imageInput) ? imageInput : [imageInput];
      
      // Use detailed model for high detail requests
      const model = options.detail === 'high' 
        ? this._getModel('VISION_DETAILED', options.model)
        : this._getModel('VISION', options.model);
      
      const content = [
        { type: "text", text: prompt },
        ...images.map(img => ({
          type: "image_url",
          image_url: {
            url: img.startsWith('data:') || img.startsWith('http') ? img : `data:image/jpeg;base64,${img}`,
            detail: options.detail || "auto"
          }
        }))
      ];

      const response = await this.client.chat.completions.create({
        model,
        messages: [{
          role: 'user',
          content
        }],
        max_tokens: options.maxTokens ?? this.defaultOptions.maxTokens,
        ...options.additionalParams
      });

      return response.choices[0].message.content;
    } catch (error) {
      console.error('Error analyzing image:', error);
      throw new Error(`Vision API Error: ${error.message}`);
    }
  }

  /**
   * Analyze image from file path (Node.js environment)
   */
  async analyzeImageFromFile(filePath, prompt = "What is in this image?", options = {}) {
    // This method is only available in Node.js environments
    if (typeof window !== 'undefined') {
      throw new Error('analyzeImageFromFile is not available in browser environment. Use analyzeImage with a URL or base64 string instead.');
    }
    
    // This code will only execute in Node.js environments
    // The dynamic import won't be bundled by webpack
    try {
      const fs = await import('fs');
      const base64Image = fs.readFileSync(filePath, 'base64');
      return this.analyzeImage(base64Image, prompt, options);
    } catch (error) {
      console.error('Error reading image file:', error);
      throw new Error(`File reading error: ${error.message}`);
    }
  }

  /**
   * Web search using search-enabled models
   */
  async webSearchCompletion(query, options = {}) {
    try {
      const response = await this.client.chat.completions.create({
        model: this._getModel('WEB_SEARCH', options.model),
        web_search_options: options.webSearchOptions || {},
        messages: [{
          role: 'user',
          content: query
        }],
        temperature: options.temperature ?? 0.5, // Lower temperature for factual search
        max_tokens: options.maxTokens ?? this.defaultOptions.maxTokens,
        ...options.additionalParams
      });

      return response.choices[0].message.content;
    } catch (error) {
      console.error('Error with web search:', error);
      throw new Error(`Web Search API Error: ${error.message}`);
    }
  }

  /**
   * Web search with streaming
   */
  async streamWebSearchCompletion(query, onChunk, options = {}) {
    try {
      const stream = await this.client.chat.completions.create({
        model: this._getModel('WEB_SEARCH', options.model),
        web_search_options: options.webSearchOptions || {},
        messages: [{
          role: 'user',
          content: query
        }],
        stream: true,
        temperature: options.temperature ?? 0.5,
        max_tokens: options.maxTokens ?? this.defaultOptions.maxTokens,
        ...options.additionalParams
      });

      let fullResponse = '';
      
      for await (const chunk of stream) {
        const content = chunk.choices[0]?.delta?.content || '';
        fullResponse += content;
        
        if (onChunk) {
          onChunk(content, fullResponse);
        }
      }

      return fullResponse;
    } catch (error) {
      console.error('Error streaming web search:', error);
      throw new Error(`Web Search Streaming Error: ${error.message}`);
    }
  }

  /**
   * Chat completion with conversation history
   */
  async getChatCompletionWithHistory(messages, options = {}) {
    try {
      const response = await this.client.chat.completions.create({
        model: this._getModel('CHAT', options.model),
        messages: messages,
        temperature: options.temperature ?? this.defaultOptions.temperature,
        max_tokens: options.maxTokens ?? this.defaultOptions.maxTokens,
        ...options.additionalParams
      });

      return response.choices[0].message.content;
    } catch (error) {
      console.error('Error calling OpenAI API with history:', error);
      throw new Error(`OpenAI API Error: ${error.message}`);
    }
  }

  /**
   * Generate embeddings
   */
  async getEmbedding(text, options = {}) {
    try {
      const response = await this.client.embeddings.create({
        model: this._getModel('EMBEDDING', options.model),
        input: text,
        ...options.additionalParams
      });

      return response.data[0].embedding;
    } catch (error) {
      console.error('Error generating embedding:', error);
      throw new Error(`OpenAI API Error: ${error.message}`);
    }
  }

  /**
   * Check if API key is configured
   */
  isApiKeyConfigured() {
    return this.client && !!this.client.apiKey;
  }

  /**
   * Update models at runtime
   */
  updateModels(models) {
    this.models = {
      ...this.models,
      ...models
    };
  }

  /**
   * Update system prompts at runtime
   */
  updateSystemPrompts(prompts) {
    this.systemPrompts = {
      ...this.systemPrompts,
      ...prompts
    };
  }

  /**
   * Generate deployment email template with different styles
   */
  async generateDeploymentEmail(deploymentOptions, clientInfo = {}, productType = 'evoml', templateStyle = 'professional', additionalContext = '', options = {}) {
    const productName = PRODUCT_DETAILS[productType]?.name || 'EvoML';
    const productDescription = PRODUCT_DETAILS[productType]?.description || 'our platform';
    
    // Get the template function and call it with the product name
    const templateFunction = DEPLOYMENT_EMAIL_TEMPLATES[templateStyle] || DEPLOYMENT_EMAIL_TEMPLATES.professional;
    const systemPrompt = templateFunction(productName);

    const userPrompt = `Generate an email template for a potential client interested in ${productName} (${productDescription}).

Client Information:
- Company: ${clientInfo.company || '[Client Company]'}
- Contact Name: ${clientInfo.contactName || '[Contact Name]'}
- Industry: ${clientInfo.industry || '[Industry]'}
- Team Size: ${clientInfo.teamSize || '[Team Size]'}
- Use Case: ${clientInfo.useCase || '[Use Case]'}

${additionalContext ? `Additional Context:\n${additionalContext}\n\n` : ''}

Selected Deployment Options:
${deploymentOptions.map(opt => `
### ${opt.title} (${opt.subtitle})
- **Security Level**: ${opt.security?.level || 'Standard'}
- **Privacy**: ${opt.privacy}
- **Infrastructure**: ${opt.id.includes('saas-shared') ? 'Shared IP, multi-tenant resources' : opt.id.includes('saas-dedicated') ? 'Dedicated IP, isolated resources' : 'Customer-managed infrastructure'}
- **LLM Integration**: ${opt.llmIntegration?.type || 'Standard'}
- **Supported LLM Providers**: ${opt.llmIntegration?.providers?.join(', ') || 'Various'}
- **Custom LLM Support**: ${opt.llmIntegration?.customSupport || 'Bring your own LLM'}
- **Data Privacy**: ${opt.llmIntegration?.dataPrivacy || 'Standard privacy controls'}
- **Key Features**: ${opt.features.slice(0, 3).join(', ')}
- **Best For**: ${opt.useCase}
`).join('\n')}

${TABLE_INSTRUCTIONS}`;

    try {
      const response = await this.getChatCompletion(userPrompt, {
        ...options,
        systemPrompt,
        temperature: 0.7,
        maxTokens: 800
      });

      return response;
    } catch (error) {
      console.error('Error generating deployment email:', error);
      throw new Error(`Failed to generate deployment email: ${error.message}`);
    }
  }

  /**
   * Stream deployment email generation
   */
  async streamDeploymentEmail(deploymentOptions, clientInfo = {}, productType = 'evoml', templateStyle = 'professional', additionalContext = '', onChunk, options = {}) {
    const productName = PRODUCT_DETAILS[productType]?.name || 'EvoML';
    const productDescription = PRODUCT_DETAILS[productType]?.description || 'our platform';
    
    // Get the template function and call it with the product name
    const templateFunction = DEPLOYMENT_EMAIL_TEMPLATES[templateStyle] || DEPLOYMENT_EMAIL_TEMPLATES.professional;
    const systemPrompt = templateFunction(productName);

    const userPrompt = `Generate an email template for a potential client interested in ${productName} (${productDescription}).

Client Information:
- Company: ${clientInfo.company || '[Client Company]'}
- Contact Name: ${clientInfo.contactName || '[Contact Name]'}
- Industry: ${clientInfo.industry || '[Industry]'}
- Team Size: ${clientInfo.teamSize || '[Team Size]'}
- Use Case: ${clientInfo.useCase || '[Use Case]'}

${additionalContext ? `Additional Context:\n${additionalContext}\n\n` : ''}

Selected Deployment Options:
${deploymentOptions.map(opt => `
### ${opt.title} (${opt.subtitle})
- **Security Level**: ${opt.security?.level || 'Standard'}
- **Privacy**: ${opt.privacy}
- **Infrastructure**: ${opt.id.includes('saas-shared') ? 'Shared IP, multi-tenant resources' : opt.id.includes('saas-dedicated') ? 'Dedicated IP, isolated resources' : 'Customer-managed infrastructure'}
- **LLM Integration**: ${opt.llmIntegration?.type || 'Standard'}
- **Supported LLM Providers**: ${opt.llmIntegration?.providers?.join(', ') || 'Various'}
- **Custom LLM Support**: ${opt.llmIntegration?.customSupport || 'Bring your own LLM'}
- **Data Privacy**: ${opt.llmIntegration?.dataPrivacy || 'Standard privacy controls'}
- **Key Features**: ${opt.features.slice(0, 3).join(', ')}
- **Best For**: ${opt.useCase}
`).join('\n')}

${TABLE_INSTRUCTIONS}`;

    try {
      return await this.streamChatCompletion(userPrompt, onChunk, {
        ...options,
        systemPrompt,
        temperature: 0.7,
        maxTokens: 800
      });
    } catch (error) {
      console.error('Error streaming deployment email:', error);
      throw new Error(`Failed to stream deployment email: ${error.message}`);
    }
  }

  /**
   * Generate deployment comparison summary
   */
  async generateDeploymentSummary(deploymentOptions, options = {}) {
    const systemPrompt = SYSTEM_PROMPTS.DEPLOYMENT_CONSULTANT;

    const userPrompt = `Create a brief comparison summary of the following EvoML deployment options:

${deploymentOptions.map(opt => `
${opt.title}:
- Infrastructure: ${opt.machines.type}
- Privacy Level: ${opt.privacy}
- Cost: ${opt.cost}
- Complexity: ${opt.complexity}
- Security: ${opt.security?.level || 'Standard'}
- Best Use Case: ${opt.useCase}
`).join('\n')}

Please provide a clear, concise summary that helps decision-makers understand which option best fits their needs.`;

    try {
      const response = await this.getChatCompletion(userPrompt, {
        ...options,
        systemPrompt,
        temperature: 0.5,
        maxTokens: 500
      });

      return response;
    } catch (error) {
      console.error('Error generating deployment summary:', error);
      throw new Error(`Failed to generate deployment summary: ${error.message}`);
    }
  }
}

// Create a default instance for backward compatibility
const defaultClient = new OpenAIClient();

// Export individual functions that use the default instance
export const getChatCompletion = (prompt, options) => defaultClient.getChatCompletion(prompt, options);
export const streamChatCompletion = (prompt, onChunk, options) => defaultClient.streamChatCompletion(prompt, onChunk, options);
export const streamChatCompletionAdvanced = (messages, onChunk, options) => defaultClient.streamChatCompletionAdvanced(messages, onChunk, options);
export const analyzeImage = (imageInput, prompt, options) => defaultClient.analyzeImage(imageInput, prompt, options);
export const analyzeImageFromFile = (filePath, prompt, options) => {
  if (typeof window !== 'undefined') {
    throw new Error('analyzeImageFromFile is not available in browser environment. Use analyzeImage with a URL or base64 string instead.');
  }
  return defaultClient.analyzeImageFromFile(filePath, prompt, options);
};
export const webSearchCompletion = (query, options) => defaultClient.webSearchCompletion(query, options);
export const streamWebSearchCompletion = (query, onChunk, options) => defaultClient.streamWebSearchCompletion(query, onChunk, options);
export const getChatCompletionWithHistory = (messages, options) => defaultClient.getChatCompletionWithHistory(messages, options);
export const getEmbedding = (text, options) => defaultClient.getEmbedding(text, options);
export const isApiKeyConfigured = () => defaultClient.isApiKeyConfigured();
export const generateDeploymentEmail = (deploymentOptions, clientInfo, productType, templateStyle, additionalContext, options) => defaultClient.generateDeploymentEmail(deploymentOptions, clientInfo, productType, templateStyle, additionalContext, options);
export const streamDeploymentEmail = (deploymentOptions, clientInfo, productType, templateStyle, additionalContext, onChunk, options) => defaultClient.streamDeploymentEmail(deploymentOptions, clientInfo, productType, templateStyle, additionalContext, onChunk, options);
export const generateDeploymentSummary = (deploymentOptions, options) => defaultClient.generateDeploymentSummary(deploymentOptions, options);

// Export utility functions
export async function imageToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function isValidImageUrl(url) {
  try {
    const urlObj = new URL(url);
    return /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(urlObj.pathname) || 
           url.startsWith('data:image/');
  } catch {
    return false;
  }
}

// Export constants
export const MODELS = {
  GPT4: 'gpt-4',
  GPT4_TURBO: 'gpt-4-turbo',
  GPT4O: 'gpt-4o',
  GPT4O_MINI: 'gpt-4o-mini',
  GPT4O_SEARCH: 'gpt-4o-search-preview',
  GPT4O_MINI_SEARCH: 'gpt-4o-mini-search-preview',
};

// Export the client class and default instance
export { OpenAIClient, defaultClient as openaiClient }; 